import { useEffect, useState } from 'react'
import Panel from './ui/Panel'
import Toggle from './ui/Toggle'
import Button from './ui/Button'
import { useToast } from './ui/Toast'
import pushTexts from '../texts.push'
import {
  currentSubscription,
  getPrefs,
  needsHomeScreenInstall,
  pushSupported,
  sendTestPush,
  setPrefs,
  subscribePush,
  unsubscribePush
} from '../lib/api.push'
import type { PushPrefs } from '../lib/api.push'

const PREF_ROWS: Array<{ key: keyof PushPrefs; label: string; hint?: string }> = [
  { key: 'gameStarted', label: pushTexts.gameStarted },
  { key: 'gameCompleted', label: pushTexts.gameCompleted, hint: pushTexts.gameCompletedHint },
  { key: 'invited', label: pushTexts.invited }
]

function Heading() {
  return <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">{pushTexts.title}</span>
}

function PushSettings() {
  const supported = pushSupported()
  const installFirst = supported && needsHomeScreenInstall()
  const [deviceOn, setDeviceOn] = useState(false)
  const [anyDevice, setAnyDevice] = useState(false)
  const [prefs, setPrefsState] = useState<PushPrefs>({ gameStarted: true, gameCompleted: true, invited: true })
  const [blocked, setBlocked] = useState(supported && Notification.permission === 'denied')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const { showToast } = useToast()

  useEffect(() => {
    if (!supported || installFirst) return
    getPrefs()
      .then(({ subscribed, ...rest }) => {
        setPrefsState(rest)
        setAnyDevice(subscribed)
      })
      .catch(() => undefined)
    currentSubscription()
      .then((subscription) => setDeviceOn(!!subscription && Notification.permission === 'granted'))
      .catch(() => undefined)
  }, [supported, installFirst])

  if (!supported) return null

  if (installFirst) {
    return (
      <Panel>
        <Heading />
        <p className="text-[0.9em] text-text-2">{pushTexts.installHint}</p>
      </Panel>
    )
  }

  const handleDevice = async (on: boolean) => {
    setError('')
    setBusy(true)
    try {
      if (on) {
        await subscribePush()
        setAnyDevice(true)
      } else {
        await unsubscribePush()
      }
      setDeviceOn(on)
    } catch {
      if (Notification.permission === 'denied') {
        setBlocked(true)
      } else {
        setError(on ? pushTexts.couldNotSubscribe : pushTexts.couldNotUnsubscribe)
      }
    } finally {
      setBusy(false)
    }
  }

  const handlePref = async (key: keyof PushPrefs, value: boolean) => {
    setError('')
    const previous = prefs
    setPrefsState({ ...prefs, [key]: value })
    try {
      await setPrefs({ [key]: value })
    } catch {
      setPrefsState(previous)
      setError(pushTexts.couldNotSavePrefs)
    }
  }

  const handleTest = async () => {
    setError('')
    try {
      const sent = await sendTestPush()
      showToast(sent > 0 ? pushTexts.testSent : pushTexts.testNone)
    } catch {
      setError(pushTexts.couldNotSendTest)
    }
  }

  return (
    <Panel>
      <Heading />
      <div className="grid divide-y divide-text-2/20">
        <div className="flex items-center justify-between gap-2.5 pb-3">
          <div>
            <b>{pushTexts.device}</b>
            <div className="text-[0.85em] text-text-2">{blocked ? pushTexts.blocked : pushTexts.deviceHint}</div>
          </div>
          <Toggle
            checked={deviceOn}
            onChange={(on) => !busy && !blocked && handleDevice(on)}
            label={pushTexts.device}
            className={blocked ? 'opacity-45' : ''}
          />
        </div>
        {(deviceOn || anyDevice) &&
          PREF_ROWS.map((row) => (
            <div key={row.key} className="flex items-center justify-between gap-2.5 py-3">
              <div>
                <b>{row.label}</b>
                {row.hint && <div className="text-[0.85em] text-text-2">{row.hint}</div>}
              </div>
              <Toggle checked={prefs[row.key]} onChange={(value) => handlePref(row.key, value)} label={row.label} />
            </div>
          ))}
      </div>
      {error && <p className="text-red">{error}</p>}
      {deviceOn && (
        <Button variant="ghost" size="sm" onClick={handleTest}>
          {pushTexts.sendTest}
        </Button>
      )}
    </Panel>
  )
}

export default PushSettings
